/**
 * 팀 프로젝트 관련 함수들
 */

import { createBrowserClient } from '@/supabase'
import { TEAM_MEMBER_STATUS } from '@/utils/team-helpers'
import type { TeamProject } from '../team-types'

/**
 * 팀이 참여 중인 프로젝트 목록 조회
 */
export const fetchTeamProjects = async (teamId: number): Promise<TeamProject[]> => {
  const supabase = createBrowserClient()

  // 1) 팀 정보 + 팀원 프로필 ID들
  const { data: team, error: teamError } = await supabase
    .from('teams')
    .select('id, manager_profile_id')
    .eq('id', teamId)
    .is('deleted_at', null)
    .maybeSingle()

  if (teamError) throw teamError
  if (!team) throw new Error('팀을 찾을 수 없습니다.')

  interface TeamBasicInfo3 {
    id: number
    manager_profile_id: string | null
  }
  const teamTyped3 = team as unknown as TeamBasicInfo3

  const { data: memberRows, error: memberError } = await supabase
    .from('team_members')
    .select('profile_id')
    .eq('team_id', teamId)
    .eq('status', TEAM_MEMBER_STATUS.ACTIVE)

  if (memberError) throw memberError

  interface MemberRow3 {
    profile_id: string | null
  }
  const memberProfileIds = ((memberRows ?? []) as unknown as MemberRow3[])
    .map((m) => m.profile_id)
    .filter(Boolean) as string[]

  const teamProfileIds = [teamTyped3.manager_profile_id, ...memberProfileIds].filter(Boolean) as string[]

  if (teamProfileIds.length === 0) return []

  // 2) 이 팀 구성원들이 참여 중인 프로젝트 조회 (estimates 기반으로 변경)
  // project_members 대신 estimates에서 ACCEPTED 상태인 견적서 조회
  // 팀 프로젝트만 조회 (proposer_team_id가 해당 팀 ID와 일치)
  const { data: estimateRows, error: pmError } = await supabase
    .from('estimates')
    .select('counsel_id, proposer_team_id, proposer_profile_id, submitted_by_profile_id, status')
    .eq('status', 'ACCEPTED')
    .eq('proposer_team_id', teamId)
    .is('withdrawn_at', null)

  if (pmError) throw pmError
  if (!estimateRows || estimateRows.length === 0) return []

  interface EstimateRow {
    counsel_id: number
    proposer_team_id: number | null
    proposer_profile_id: string | null
    submitted_by_profile_id: string | null
    status: string | null
  }
  const estimateRowsTyped = (estimateRows ?? []) as unknown as EstimateRow[]

  const counselIds = Array.from(new Set(estimateRowsTyped.map((est) => est.counsel_id))).filter(
    Boolean,
  ) as number[]

  // 3) 프로젝트(counsel) 정보 조회
  const { data: counselRows, error: counselError } = await supabase
    .from('counsel')
    .select('counsel_id, title, counsel_status, cost')
    .in('counsel_id', counselIds)
    .is('deleted_at', null)

  if (counselError) throw counselError

  interface CounselRow {
    counsel_id: number
    title: string | null
    counsel_status: string | null
    cost: string | null
  }

  const counselById = ((counselRows ?? []) as unknown as CounselRow[]).reduce(
    (acc, cur) => ({
      ...acc,
      [cur.counsel_id]: cur,
    }),
    {} as Record<number, CounselRow>,
  )

  // 4) counsel_id 기준으로 멤버 묶기
  const projectsMap: Record<number, TeamProject> = {}

  for (const est of estimateRowsTyped) {
    const counsel = counselById[est.counsel_id]
    if (!counsel) continue

    if (!projectsMap[est.counsel_id]) {
      projectsMap[est.counsel_id] = {
        counsel_id: counsel.counsel_id,
        title: counsel.title,
        status: counsel.counsel_status,
        budget: counsel.cost ? parseFloat(counsel.cost) : null,
        members: [],
      }
    }

    // 팀 프로젝트이므로 팀 구성원들을 멤버로 추가
    // 프로필 ID는 proposer_profile_id 또는 submitted_by_profile_id 사용
    const profileId = est.proposer_profile_id || est.submitted_by_profile_id
    if (profileId && teamProfileIds.includes(profileId)) {
      // 팀 매니저인지 확인하여 역할 결정
      const isManager = teamTyped3.manager_profile_id === profileId
      projectsMap[est.counsel_id].members.push({
        profile_id: profileId,
        role: isManager ? 'MANAGER' : 'MAKER',
      })
    }
  }

  return Object.values(projectsMap)
}

